/**
 * SandboxStep — 10-сек демо-счётчик.
 *
 * Параметры приходят ИЗВНЕ (CalculatorStep принимает решения до
 * запуска демо — сумма + «развивать сеть» ли). Здесь только
 * анимация по заданным props.
 *
 * - Счётчик стейка растёт до `demoAmount × 15%` за 10 сек (linear)
 * - Если `withNetwork=true` — вторая полоска растёт до $15 000
 *   за те же 10 сек (ease-out-cubic — быстрый старт, замедление)
 * - При withNetwork — мелькают статусы Luna:
 *   Линк → Хаб → Сектор → Центурион → Император
 */
import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { lunaApi } from '@/services/api';
import { EASE_SCREEN, SPRING_CARD } from '../springs';
import { useSounds } from '../useSounds';

const DEMO_DURATION_SEC = 10;
const MONTHLY_RATE = 0.15;
const NETWORK_DEMO_TARGET = 15000;

const NETWORK_STATUSES: Array<{ t: number; nameKey: string; icon: string }> = [
  { t: 1500, nameKey: 'onboarding.sandbox.status1', icon: '🔗' },
  { t: 3500, nameKey: 'onboarding.sandbox.status2', icon: '🛰' },
  { t: 5500, nameKey: 'onboarding.sandbox.status3', icon: '🌐' },
  { t: 7500, nameKey: 'onboarding.sandbox.status4', icon: '⚔️' },
  { t: 9200, nameKey: 'onboarding.sandbox.status5', icon: '👑' },
];

interface SandboxStepProps {
  demoAmount: number;
  withNetwork: boolean;
  onFinish: () => void;
}

function fmt(v: number, locale: string): string {
  return `+$${v.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function SandboxStep({
  demoAmount,
  withNetwork,
  onFinish,
}: SandboxStepProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const { haptic } = useTelegramWebApp();
  const sounds = useSounds();

  const stakeMv = useMotionValue(0);
  const networkMv = useMotionValue(0);
  const stakeDisplay = useTransform(stakeMv, (v) => fmt(v, locale));
  const networkDisplay = useTransform(networkMv, (v) => fmt(v, locale));

  const [activeStatusIdx, setActiveStatusIdx] = useState(-1);
  const finishedRef = useRef(false);

  const stakeTarget = demoAmount * MONTHLY_RATE;

  useEffect(() => {
    void lunaApi.onboarding.sandboxEvent('started');

    const stopEarningSound = sounds.sandboxEarning();

    // Счётчик стейка — линейный, через motion (GPU)
    const stakeCtl = animate(stakeMv, stakeTarget, {
      duration: DEMO_DURATION_SEC,
      ease: 'linear',
    });

    // Счётчик сети — ease-out-cubic
    let networkCtl: ReturnType<typeof animate> | null = null;
    const statusTimers: number[] = [];
    if (withNetwork) {
      networkCtl = animate(networkMv, NETWORK_DEMO_TARGET, {
        duration: DEMO_DURATION_SEC,
        ease: [0.22, 1, 0.36, 1],
      });
      for (let i = 0; i < NETWORK_STATUSES.length; i++) {
        const id = window.setTimeout(() => {
          setActiveStatusIdx(i);
          haptic.light();
        }, NETWORK_STATUSES[i]!.t);
        statusTimers.push(id);
      }
    }

    const finishTimer = window.setTimeout(() => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      haptic.success();
      sounds.success();
      void lunaApi.onboarding.sandboxEvent('finished');
      onFinish();
    }, DEMO_DURATION_SEC * 1000 + 200);

    return () => {
      stakeCtl.stop();
      networkCtl?.stop();
      for (const t of statusTimers) window.clearTimeout(t);
      window.clearTimeout(finishTimer);
      stopEarningSound();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="relative w-full h-full flex flex-col items-center justify-start px-5 pt-10 pb-12 gap-5"
    >
      {/* CSS @keyframes для обеих полосок — браузер крутит на GPU,
          без React setState каждый кадр. Плавные 60 fps даже на
          слабых Android. */}
      <style>{`
        @keyframes sandboxStakeFill {
          from { width: 0%; }
          to   { width: 100%; }
        }
        @keyframes sandboxNetworkFill {
          /* ease-out-cubic — быстрый рост в начале, замедление к концу.
             Делаем через key frames чтоб не зависеть от CSS easing. */
          0%   { width: 0%; }
          10%  { width: 27.1%; }
          20%  { width: 48.8%; }
          30%  { width: 65.7%; }
          40%  { width: 78.4%; }
          50%  { width: 87.5%; }
          60%  { width: 93.6%; }
          70%  { width: 97.3%; }
          80%  { width: 99.2%; }
          90%  { width: 99.9%; }
          100% { width: 100%; }
        }
        .sandbox-stake-bar {
          animation: sandboxStakeFill ${DEMO_DURATION_SEC}s linear forwards;
          will-change: width;
        }
        .sandbox-network-bar {
          animation: sandboxNetworkFill ${DEMO_DURATION_SEC}s linear forwards;
          will-change: width;
        }
      `}</style>

      <div className="text-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.85 }}
          transition={EASE_SCREEN}
          className="font-mono"
          style={{
            fontSize: 10,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'rgba(220,230,255,0.6)',
          }}
        >
          {t('onboarding.sandbox.kicker')}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...EASE_SCREEN, delay: 0.1 }}
          className="mt-2 text-[22px] leading-tight font-bold"
          style={{ color: 'rgba(245,250,255,0.98)', letterSpacing: '-0.02em' }}
        >
          {t('onboarding.sandbox.title')}
        </motion.h2>
        <p
          className="mt-1 text-[11px]"
          style={{ color: 'rgba(200,210,230,0.6)' }}
        >
          {t('onboarding.sandbox.contractHint', {
            amount: demoAmount.toLocaleString(locale),
            network: withNetwork ? t('onboarding.sandbox.withNetwork') : '',
          })}
        </p>
      </div>

      {/* Монета */}
      <div className="relative w-[180px] h-[180px] flex items-center justify-center">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at 50% 50%, rgba(120,220,255,0.45) 0%, rgba(120,220,255,0.18) 30%, transparent 65%)',
            filter: 'blur(12px)',
          }}
        />
        <motion.img
          src="/onboarding/coin_usdt.webp"
          alt=""
          animate={{
            rotate: [0, 6, -6, 0],
            scale: [1, 1.02, 1],
          }}
          transition={{
            duration: 3,
            ease: 'easeInOut',
            repeat: Infinity,
          }}
          className="relative w-[140px] h-[140px] object-contain"
        />
      </div>

      {/* СТЕЙК — счётчик + полоска */}
      <div className="w-full max-w-[320px] text-center">
        <p
          className="font-mono"
          style={{
            fontSize: 9,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'rgba(200,210,230,0.55)',
          }}
        >
          {t('onboarding.sandbox.contractIncome')}
        </p>
        <motion.div
          style={{
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.03em',
            fontSize: 36,
            fontWeight: 800,
            color: '#f5e6a8',
            textShadow: '0 0 30px rgba(234,196,96,0.45)',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
          }}
        >
          {stakeDisplay}
        </motion.div>
        <div
          className="mt-2 h-[6px] rounded-full overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          <div
            className="sandbox-stake-bar h-full rounded-full"
            style={{
              background: 'linear-gradient(90deg, #f5e6a8 0%, #e9c76a 100%)',
              boxShadow: '0 0 10px rgba(234,196,96,0.55)',
            }}
          />
        </div>
      </div>

      {/* СЕТЬ — вторая полоска + счётчик + мелькающие статусы */}
      {withNetwork && (
        <div className="w-full max-w-[320px] text-center">
          <p
            className="font-mono"
            style={{
              fontSize: 9,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'rgba(200,210,230,0.55)',
            }}
          >
            {t('onboarding.sandbox.networkIncome')}
          </p>
          <motion.div
            style={{
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-0.03em',
              fontSize: 30,
              fontWeight: 800,
              color: '#C9B8FF',
              textShadow: '0 0 24px rgba(124,92,255,0.5)',
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
            }}
          >
            {networkDisplay}
          </motion.div>
          <div
            className="mt-2 h-[6px] rounded-full overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          >
            <div
              className="sandbox-network-bar h-full rounded-full"
              style={{
                background: 'linear-gradient(90deg, #9B7FFF 0%, #6A4BFF 100%)',
                boxShadow: '0 0 10px rgba(124,92,255,0.55)',
              }}
            />
          </div>

          <div
            className="mt-3 flex items-center justify-center"
            style={{ minHeight: 28 }}
          >
            <AnimatePresence mode="wait">
              {activeStatusIdx >= 0 && (
                <motion.div
                  key={activeStatusIdx}
                  initial={{ opacity: 0, y: 8, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.9 }}
                  transition={{ duration: 0.3, ...SPRING_CARD }}
                  className="inline-flex items-center gap-1.5 rounded-full
                             px-3 py-1"
                  style={{
                    background:
                      'color-mix(in oklab, #7C5CFF 18%, transparent)',
                    border: '1px solid rgba(124,92,255,0.4)',
                    color: '#E9E4FF',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                  }}
                >
                  <span>{NETWORK_STATUSES[activeStatusIdx]!.icon}</span>
                  <span>
                    {t('onboarding.sandbox.statusPrefix', { name: t(NETWORK_STATUSES[activeStatusIdx]!.nameKey) })}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
}
